type YoutubeBool = 1 | 0;

export interface YoutubeEvent<D> {
    target: Player,
    data: D,
}

export interface CreateVideoOptions {
    videoId: string,
    autoplay?: boolean,
    playsInline?: boolean,
    controls?: boolean,
    modestBranding?: boolean,
    muted?: boolean,
    width?: number | string,
    height?: number | string,
}

export enum PlaybackStatus {
    Unstarted = -1,
    Ended = 0,
    Playing = 1,
    Paused = 2,
    Buffering = 3,
    VideoCued = 5,
}

interface YoutubeEventMap {
    "onStateChange": YoutubeEvent<PlaybackStatus>;
    "onError": YoutubeEvent<number>;
    "onReady": YoutubeEvent<void>;
}

export interface Player {
    destroy(): void;
    hideVideoInfo(): void;
    isMuted(): boolean;
    mute(): void;
    playVideo(): void;
    stopVideo(): void;
    pauseVideo(): void;
    getPlayerState(): PlaybackStatus;
    addEventListener<K extends keyof YoutubeEventMap>(type: K, listener: (event: YoutubeEventMap[K]) => any);
    removeEventListener<K extends keyof YoutubeEventMap>(type: K, listener: (event: YoutubeEventMap[K]) => any);
}

const createYoutubeBool = (bool: boolean | undefined): YoutubeBool => {
    if (bool === true) {
        return 1;
    }

    if (bool === false) {
        return 0;
    }

    return undefined;
};

class PlayerManager {
    private players: Player[] = [];

    private onStateChange = (event: YoutubeEvent<PlaybackStatus>) => {
        if (event.data == PlaybackStatus.Playing && !event.target.isMuted()) {
            for (const player of this.players) {
                if (player == event.target) {
                    continue; // Skip if player is the current target
                }

                if (player.getPlayerState() == PlaybackStatus.Playing && !player.isMuted()) {
                    player.pauseVideo();
                }
            }
        }
    };

    register(player: Player) {
        this.players.push(player);
        player.addEventListener('onStateChange', this.onStateChange);
    }

    unregister(player: Player) {
        const indexOf = this.players.findIndex((p) => p == player);
        player.removeEventListener('onStateChange', this.onStateChange);
        if (indexOf != -1) {
            this.players.splice(indexOf, 1);
        }
    }
}

class YoutubeSdkLoader {
    private callbacks: (() => void)[] = [];
    private loaded = false;
    private registerLoadCallback(callback: () => void) {
        this.callbacks.push(callback);

        window['onYouTubeIframeAPIReady'] = () => {
            this.loaded = true;
            for (const callback of this.callbacks) {
                callback();
            }
            this.callbacks = [];
        };
    }

    private loadScript() {
        return new Promise<void>((resolve) => {
            if (!(document.querySelector('#youtube-sdk') instanceof HTMLScriptElement)) {
                const firstScript = document.getElementsByName('script')[0];
                const script = document.createElement('script');
                script.id = 'youtube-sdk';
                script.src = "https://www.youtube.com/iframe_api";
                if (firstScript) {
                    document.insertBefore(firstScript, script);
                } else {
                    document.head.append(script);
                }
            }

            if (this.loaded) {
                resolve();
            } else {
                this.registerLoadCallback(resolve);
            }
        });
    }

    private createPlayer(elementId: string, options: CreateVideoOptions): Promise<Player> {
        return new Promise((resolve) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            new YT.Player(elementId, {
                videoId: options.videoId,
                width: options.width,
                height: options.height,
                playerVars: {
                    enablejsapi: createYoutubeBool(true),
                    muted: createYoutubeBool(options.muted),
                    autoplay: createYoutubeBool(options.autoplay),
                    modestbranding: createYoutubeBool(options.modestBranding),
                    playsinline: createYoutubeBool(options.playsInline),
                    controls: createYoutubeBool(options.controls),
                },
                events: {
                    onReady: (event: YoutubeEvent<void>) => {
                        resolve(event.target);
                    },
                },
            });
        });
    }

    async load(elementId: string, options: CreateVideoOptions): Promise<Player> {
        await this.loadScript();
        return await this.createPlayer(elementId, options);
    }
}

export default class YoutubePlayerManager {
    private static readonly playerManager = new PlayerManager();
    private static readonly sdkLoader = new YoutubeSdkLoader();

    static async load(elementId: string, options: CreateVideoOptions): Promise<Player> {
        return this.sdkLoader.load(elementId, options);
    }

    static register(player: Player) {
        this.playerManager.register(player);
    }

    static unregister(player: Player) {
        this.playerManager.unregister(player);
    }
}

